import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './GaleriaProducto.module.css';

const PlaceholderLarge = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" width="96" height="96" fill="none">
    <rect x="12" y="30" width="72" height="54" rx="4" stroke="#cbd5e1" strokeWidth="3" />
    <polyline points="12,30 48,12 84,30" stroke="#cbd5e1" strokeWidth="3" strokeLinejoin="round" />
    <line x1="48" y1="12" x2="48" y2="30" stroke="#cbd5e1" strokeWidth="3" />
    <line x1="12" y1="30" x2="84" y2="30" stroke="#cbd5e1" strokeWidth="3" />
  </svg>
);

const MAX_IMAGES = 4;

export const GaleriaProducto = ({ slug, productName }) => {
  const [validImages, setValidImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // We attempt to load all 4 images. If they trigger onLoad, they are valid.
  useEffect(() => {
    setIsLoading(true);
    let promises = [];

    for (let i = 1; i <= MAX_IMAGES; i++) {
      promises.push(new Promise((resolve) => {
        const img = new Image();
        const src = `/imagenes/productos/${slug}-${i}.webp`;
        img.src = src;
        img.onload = () => resolve({ src, valid: true });
        img.onerror = () => resolve({ src, valid: false });
      }));
    }

    Promise.all(promises).then(results => {
      const valid = results.filter(r => r.valid).map(r => r.src);
      setValidImages(valid);
      setActiveIndex(0);
      setIsLoading(false);
    });
  }, [slug]);

  if (isLoading) {
    return <div className={styles.galleryContainer}><div className={styles.loadingSkeleton}></div></div>;
  }

  if (validImages.length === 0) {
    return (
      <div className={`${styles.galleryContainer} ${styles.galleryEmpty}`}>
        <PlaceholderLarge />
        <p>Imagen no disponible</p>
      </div>
    );
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveIndex(prev => (prev === 0 ? validImages.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveIndex(prev => (prev === validImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <div className={styles.galleryContainer}>
        <div className={styles.mainImageArea}>
          {validImages.length > 1 && (
            <button className={`${styles.navBtn} ${styles.prevBtn}`} onClick={handlePrev}>❮</button>
          )}
          
          <div className={styles.mainImageWrapper} onClick={() => setIsZoomOpen(true)}>
            <img 
              src={validImages[activeIndex]} 
              alt={`${productName} - Vista ${activeIndex + 1}`} 
              className={styles.mainImage} 
            />
            <div className={styles.zoomHint}>
              <span>🔍 Zoom</span>
            </div>
          </div>

          {validImages.length > 1 && (
            <button className={`${styles.navBtn} ${styles.nextBtn}`} onClick={handleNext}>❯</button>
          )}
        </div>

        {validImages.length > 1 && (
          <div className={styles.thumbnailStrip}>
            {validImages.map((src, idx) => (
              <button 
                key={src} 
                className={`${styles.thumbnailBtn} ${idx === activeIndex ? styles.thumbnailActive : ''}`}
                onClick={() => setActiveIndex(idx)}
              >
                <img src={src} alt={`Miniatura ${idx + 1}`} className={styles.thumbnailImg} />
              </button>
            ))}
          </div>
        )}
      </div>

      {isZoomOpen && createPortal(
        <div className={styles.zoomModal} onClick={() => setIsZoomOpen(false)}>
          <button className={styles.closeZoom}>×</button>
          <img 
            src={validImages[activeIndex]} 
            alt={`${productName} - Zoom`} 
            className={styles.zoomedImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default GaleriaProducto;
